import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function ensureUserDoc(user) {
    const ref = doc(db, 'users', user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email,
        isAdmin: false,
        createdAt: new Date().toISOString(),
      });
    }
    return (await getDoc(ref)).data();
  }

  async function loginWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    return ensureUserDoc(result.user);
  }

  async function loginWithEmail(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return ensureUserDoc(result.user);
  }

  async function registerWithEmail(email, password, displayName) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    // Send verification email (non-blocking — user can still use app)
    sendEmailVerification(result.user).catch(err => console.warn('Verification email:', err.message));
    return ensureUserDoc({ ...result.user, displayName });
  }

  function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  async function getToken() {
    if (!currentUser) return null;
    return currentUser.getIdToken();
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const profile = await ensureUserDoc(user);
          setUserProfile(profile);
        } catch (e) {
          console.error('Error loading user profile', e);
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  const value = {
    currentUser,
    userProfile,
    isAdmin: userProfile?.isAdmin || false,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    logout,
    getToken,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
